import CredentialModalForm from 'new/components/CredentialModalForm/CredentialModalForm';
import {
  oauthServerFlow,
  filterOAuthFormValues,
  buildOAuthTemplateConfig,
} from 'new/services/oauthServerFlow';
import {
  hasOAuthTriggerField,
  isServerOAuthButton,
  isServerOAuthConnector,
  resolveTemplateAuthType,
  userSuppliesOAuthApp,
} from 'new/utils/serverOAuthEligibility';
import { withNexlaSessionToken } from 'new/utils/nexlaSessionToken';
import { withSnackbar } from 'js/utils/withSnackbar';
import PropTypes from 'prop-types';
import React from 'react';
import { Alert } from '@mui/material';
import UIUtils from 'js/lib/UIUtils';
import OAuth1GenericRESTService from '../../../../services/OAuth1GenericRESTService';
import OAuth1aService from '../../../../services/OAuth1aService';
import OAuth2GenericRESTService from '../../../../services/OAuth2GenericRESTService';
import OAuth2Service from '../../../../services/OAuth2Service';
import BigQueryService from '../../../../services/BigQueryService';
import BoxService from '../../../../services/BoxService';
import DropboxService from '../../../../services/DropboxService';
import GCSService from '../../../../services/GCSService';
import GDriveService from '../../../../services/GDriveService';
import IntuitService from '../../../../services/IntuitService';
import PubSubService from '../../../../services/PubSubService';
import SharePointService from 'js/services/SharePointService';
import ZendeskService from '../../../../services/ZendeskService';
import { INTUIT_QB_VENDOR_NAME } from 'js/constants/config';

import {
  CONNECTION_CATEGORY_TEMPLATIZED_API,
  CONNECTION_CATEGORY_TEMPLATIZED_API_MULTI,
} from 'js/constants/connectionCategories';
import {
  BOX_REST_TEMPLATE,
  GBQ_TEMPLATE,
  GCS_TEMPLATE,
  GDRIVE_TEMPLATE,
  INTUIT_QUICKBOOKS_REST_TEMPLATE,
  OAUTH1_GENERIC_REST_TEMPLATE,
  OAUTH1a_REST_TEMPLATE,
  OAUTH2_GENERIC_REST_TEMPLATE,
  OAUTH2_REST_TEMPLATE,
  PUBSUB_TEMPLATE,
  SHAREPOINT_TEMPLATE,
  ZENDESK_REST_TEMPLATE,
} from 'js/constants/templateTypes';
import JSONUtils from 'js/lib/JSONUtils';

import { cloneDeep, filter, get, has, isEmpty, map, mapKeys } from 'lodash';
import { getFeatureFlagValue } from 'new/constants/featureFlags';
import { API_SERVER_URL } from 'js/constants/api';

const headingIcon = (
  <svg
    style={{ position: 'absolute', top: '-5px' }}
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
  >
    <path
      style={{ fill: '#fff' }}
      d="M12.65 10C11.83 7.67 9.61 6 7 6c-3.31 0-6 2.69-6 6s2.69 6 6 6c2.61 0 4.83-1.67 5.65-4H17v4h4v-4h2v-4H12.65zM7 14c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2z"
    />
  </svg>
);

class NewCredentialsForm extends React.Component {
  constructor(props) {
    super(props);
    this.state = { oauthAuthorized: false };
    this._handleFormFieldUpdated = this._handleFormFieldUpdated.bind(this);
    this._handleButtonFormFieldClicked = this._handleButtonFormFieldClicked.bind(this);
  }

  _onOAuthSuccess = () => {
    this.setState({ oauthAuthorized: true });
  };

  render() {
    const { templates, connectionAuthFields } = this.props;
    const fieldClassNames = [];
    const fields = [];
    const fieldsMeta = cloneDeep(this.props.connectionAuthMeta);

    if (this.props.connectionCategory) {
      fieldClassNames.push(`auth-${this.props.connectionCategory}`);
    }

    if (this.props.connectionType) {
      fieldClassNames.push(`auth-${this.props.connectionType}`);

      // get vendors matching this connectionType and list them in API Type field
      const vendorOptions = map(
        filter(
          this.props.connectionMetas,
          ({ connectionType, connectionCategory }) =>
            connectionType === this.props.connectionType &&
            [
              CONNECTION_CATEGORY_TEMPLATIZED_API,
              CONNECTION_CATEGORY_TEMPLATIZED_API_MULTI,
            ].includes(connectionCategory),
        ),
        (meta) => ({ label: meta.displayName, value: meta.key }),
      );

      if (!isEmpty(vendorOptions)) {
        const connectionName = this.props.connectionMetas[this.props.connectionType].displayName;

        fields.push({
          name: '_vendorConnectionType',
          display_name: 'API Type',
          type: 'string',
          inputType: 'select',
          required: true,
          default: this.props.connectionType,
          onChange: this.props.onChangeApiType,
          values: [
            {
              label: `Generic ${connectionName}`,
              value: this.props.connectionType,
            },
            ...vendorOptions,
          ],
        });
        fieldsMeta.basic.unshift('_vendorConnectionType');
      }

      // add oauth2 fields
      const vendorMeta =
        this.props.connectionMetas[
          (this.props.selectedCredentialInfo &&
            this.props.selectedCredentialInfo._vendorConnectionType) ||
            this.props.connectionType
        ];

      const vendor = this.props.vendors[vendorMeta && vendorMeta.vendorId];

      const authType = (() => {
        const vendorAuthType = get(vendor, 'config.authType');
        if (!vendorAuthType) return undefined;
        if (typeof vendorAuthType === 'string') return vendorAuthType;
        const template =
          templates?.find((t) => t.id === Number(this.props.selectedTemplate)) || templates?.[0];
        return vendorAuthType[template?.name] || vendorAuthType;
      })();

      const hasCustomAuthButton = connectionAuthFields.some(
        (f) =>
          f.inputType === 'button' &&
          typeof f.value === 'string' &&
          f.value.endsWith('.fetch_token'),
      );

      if (authType === 'oauth2' && !hasCustomAuthButton) {
        fields.push(
          {
            name: 'oauth2.fetch_token_button',
            display_name: 'Authorize',
            description: 'Fetch Authentication Token',
            type: 'button',
            required: true,
            order: 30,
            conditionalDisplay: true,
            displayOn: [
              {
                name: 'oauth2.access_token',
                value: '',
              },
            ],

            key: 'oauth2.fetch_token_button',
            inputType: 'button',
          },
          {
            name: 'oauth2.access_token',
            display_name: 'Access Token',
            description: 'OAuth 2 Access Token',
            type: 'string',
            required: true,
            order: null,
            isHiddenField: true,
            key: 'oauth2.access_token',
            inputType: 'text',
          },
        );
        fieldsMeta.basic.push('oauth2.access_token', 'oauth2.fetch_token_button');
      }

      if (authType === 'oauth1a' && !hasCustomAuthButton) {
        fields.push(
          {
            name: 'oauth1a.fetch_token_button',
            display_name: 'Authorize',
            description: 'Fetch Authentication Token',
            type: 'button',
            required: true,
            order: 30,
            conditionalDisplay: true,
            displayOn: [
              {
                name: 'oauth1a.access_token',
                value: '',
              },
            ],

            key: 'oauth1a.fetch_token_button',
            inputType: 'button',
          },
          {
            name: 'oauth1a.access_token',
            display_name: 'Access Token',
            description: 'OAuth 1a Access Token',
            type: 'string',
            required: true,
            order: null,
            isHiddenField: true,
            key: 'oauth1a.access_token',
            inputType: 'text',
          },
        );
        fieldsMeta.basic.push('oauth1a.access_token', 'oauth1a.fetch_token_button');
      }
    }

    fields.push(
      {
        id: 'cred_name',
        name: 'cred_name',
        display_name: 'Credential Name',
        required: true,
      },
      {
        id: 'cred_desc',
        name: 'cred_desc',
        display_name: 'Credential Description',
        required: false,
        style: { marginBottom: 30 },
      },
    );

    if (templates?.length > 1) {
      fields.push({
        id: 0,
        display_name: 'Authentication Type',
        inputType: 'select',
        name: 'template',
        type: 'string',
        // templates are already in display order; don't re-sort by label
        unsorted: true,
        default: `${this.props.selectedTemplate || templates[0].id}`,
        options: templates.map((template) => ({
          label: template.display_name || 'Auth Template ' + template.id,
          value: `${template.id}`,
        })),
        onChange: this.props.onChangeTemplate,
      });
      fieldsMeta.basic.unshift('template');
    }
    fieldsMeta.basic.unshift('cred_name', 'cred_desc');
    fields.push(...connectionAuthFields);
    const fieldsMap = mapKeys(fields, 'name');
    fieldsMeta.basic = fieldsMeta.basic.filter((name) => !!fieldsMap[name]);

    // Ensure parent field defaults are present so shouldRenderField can
    // evaluate dependent children. Without this, the dropdown can visually
    // show a default (e.g. "Access Key") via field.default, but the
    // credentialData object lacks the key → dependent fields stay hidden.
    // See: credential-dependent-fields.spec.ts for the repro.
    const dataWithDefaults = UIUtils.configWithMissingDefaults(
      this.props.selectedCredentialInfo || {},
      connectionAuthFields,
    );

    const { oauthAuthorized } = this.state;
    // Only when admin-api is the party being redirected to. An oauth1a form still dances in
    // the browser, so admin-api's URL would be the wrong thing to register.
    const showRedirectUrl =
      getFeatureFlagValue('UI_OAUTH_SERVER_FLOW') &&
      fields.some((f) => f.name === 'oauth2.fetch_token_button') &&
      userSuppliesOAuthApp(fields) &&
      this._usesServerOAuth(
        'oauth2.fetch_token_button',
        undefined,
        this.props.selectedCredentialInfo,
      );

    return (
      <CredentialModalForm
        heading="Add New Credential"
        headingIcon={headingIcon}
        fields={fields}
        fieldsMeta={fieldsMeta}
        data={dataWithDefaults}
        buttonsDisabled={this.props.isFormAwaitingValidation || this.props.loading}
        topContent={
          showRedirectUrl ? (
            <Alert severity="info" sx={{ marginBottom: 'var(--spacing-sm)' }}>
              Redirect URL: <strong>{API_SERVER_URL}/oauth/callback</strong> — add this to your
              OAuth app&apos;s allowed redirect URLs.
            </Alert>
          ) : oauthAuthorized ? (
            <Alert severity="success" sx={{ marginBottom: 'var(--spacing-sm)' }}>
              Authorization successful — click Save to create the credential.
            </Alert>
          ) : null
        }
        onCloseClicked={this.props.onCloseClicked}
        onFieldUpdated={this._handleFormFieldUpdated}
        onButtonFieldClicked={this._handleButtonFormFieldClicked}
        onSubmit={this.props.onSaveNewCredential}
        isCredentialsForm={true}
        fieldClassNames={fieldClassNames}
      />
    );
  }

  _handleFormFieldUpdated(name, value, data) {
    this.props.onCredInfoUpdated(data, name, value);
  }

  _handleButtonFormFieldClicked(name, value, data) {
    // Hand off to admin-api only for flows it can actually run; everything else falls through
    // to the legacy dispatch, identical to its behaviour with the flag off.
    if (getFeatureFlagValue('UI_OAUTH_SERVER_FLOW') && this._usesServerOAuth(name, value, data)) {
      this._authenticateVendorServerFlow(data);
      return;
    }
    if (value === 'rest.template_type' && has(data, 'rest.template_type')) {
      this._authenticateVendor(data['rest.template_type'], data);
      return;
    }
    if (name === 'oauth2.fetch_token_button') {
      this._authenticateVendor(OAUTH2_REST_TEMPLATE, data);
    } else if (name === 'oauth2.generic_rest.fetch_token_button') {
      this._authenticateVendor(OAUTH2_GENERIC_REST_TEMPLATE, data);
    } else if (name === 'oauth1a.fetch_token_button') {
      this._authenticateVendor(OAUTH1a_REST_TEMPLATE, data);
    } else if (name === 'oauth1.generic_rest.fetch_token_button') {
      this._authenticateVendor(OAUTH1_GENERIC_REST_TEMPLATE, data);
    } else if (name === 'zendesk.fetch_token_button') {
      this._authenticateVendor(ZENDESK_REST_TEMPLATE, data);
    } else if (name === 'box.fetch_token_button') {
      this._authenticateVendor(BOX_REST_TEMPLATE, data);
    } else if (name === 'gbq.fetch_token_button') {
      this._authenticateVendor(GBQ_TEMPLATE, data);
    } else if (name === 'gcs.fetch_token_button') {
      this._authenticateVendor(GCS_TEMPLATE, data);
    } else if (name === 'gdrive.fetch_token_button') {
      this._authenticateVendor(GDRIVE_TEMPLATE, data);
    } else if (name === 'sharepoint.fetch_token_button') {
      this._authenticateVendor(SHAREPOINT_TEMPLATE, data);
    } else if (name === 'google_pubsub.fetch_token_button') {
      this._authenticateVendor(PUBSUB_TEMPLATE, data);
    } else if (name === 'intuit_qb.fetch_token_button') {
      this._authenticateVendor(INTUIT_QUICKBOOKS_REST_TEMPLATE, data);
    } else {
      switch (value) {
        case 'dropbox.fetch_token':
          DropboxService.authenticate((authInfo) => {
            if (authInfo && has(authInfo, 'access_token')) {
              const batchUpdateInfo = DropboxService.createInfoFromAuthResult(authInfo);
              this._handleFormFieldUpdated(
                'vendor.name',
                'dropbox',
                { ...data, ...batchUpdateInfo },
                batchUpdateInfo,
              );
              this._onOAuthSuccess();
            }
          }, data);
          break;

        case 'box.fetch_token':
          BoxService.authenticate((authInfo) => {
            if (authInfo && has(authInfo, 'access_token')) {
              const batchUpdateInfo = BoxService.createInfoFromAuthResult(authInfo);
              this._handleFormFieldUpdated(
                'vendor.name',
                'box',
                { ...data, ...batchUpdateInfo },
                batchUpdateInfo,
              );
              this._onOAuthSuccess();
            }
          }, data);
          break;

        default:
          break;
      }
    }
  }

  /**
   * Server-side dance. Only reached once `_handleButtonFormFieldClicked` has confirmed the
   * connector and the clicked control are both something admin-api can run.
   */
  _authenticateVendorServerFlow(data) {
    const effectiveConnectionType = data._vendorConnectionType || this.props.connectionType;
    const vendorMeta = this.props.connectionMetas?.[effectiveConnectionType];
    const vendorId = vendorMeta?.vendorId;
    const authTemplateId = this.props.selectedTemplate
      ? Number(this.props.selectedTemplate)
      : undefined;
    const credentialName = data.cred_name || effectiveConnectionType;

    const formValues = filterOAuthFormValues(data);

    oauthServerFlow({
      nexlaSessionToken: this.props.nexlaSessionToken,
      vendorId,
      authTemplateId,
      credentialName,
      formValues: Object.keys(formValues).length > 0 ? formValues : undefined,
      // Mode B: the API returns tokens, the form is populated, the user clicks Save.
    })
      .then((result) => {
        if (result.type !== 'tokens') {
          this.props.showError('Authorization did not return credentials. Please try again.');
          return;
        }
        // Namespace the tokens the way the credential model expects before merging — Save
        // copies this into template_config. Template-derived fields are the API's job,
        // resolved via `enrich_config`.
        const merged = {
          ...this.props.selectedCredentialInfo,
          ...buildOAuthTemplateConfig({}, result.tokens, this.props.connectionAuthFields),
        };
        this.props.onCredInfoUpdated?.(merged, null, null);
        this._onOAuthSuccess();
      })
      .catch((err) => {
        this.props.showError(err.message || 'Authorization failed.');
      });
  }

  /** Connector + control both eligible for the server-side dance. */
  _usesServerOAuth(name, value, data) {
    if (!isServerOAuthButton(name, value)) return false;

    const connectionType = data?._vendorConnectionType || this.props.connectionType;
    const vendorMeta = this.props.connectionMetas?.[connectionType];
    const vendor = this.props.vendors?.[vendorMeta?.vendorId];
    const selected =
      this.props.templates?.find((t) => t.id === Number(this.props.selectedTemplate)) ??
      this.props.templates?.[0];

    return isServerOAuthConnector({
      connectionCategory: vendorMeta?.connectionCategory ?? this.props.connectionCategory,
      hasOAuthTrigger: hasOAuthTriggerField(this.props.connectionAuthFields),
      authType: resolveTemplateAuthType(vendor?.config?.authType, selected?.name),
    });
  }

  _authenticateVendor(vendor, data) {
    switch (vendor) {
      case OAUTH2_REST_TEMPLATE:
        OAuth2Service.authenticate((authInfo, error) => {
          const batchUpdateInfo = {
            ...data,
            ...mapKeys(authInfo, (value, key) => `oauth2.${key}`),
          };
          if (authInfo && has(authInfo, 'access_token')) {
            this._handleFormFieldUpdated(
              'oauth2.access_token',
              authInfo.access_token,
              batchUpdateInfo,
            );
            this._onOAuthSuccess();
          } else {
            console.log(error);
          }
        }, data);
        break;

      case OAUTH2_GENERIC_REST_TEMPLATE:
        OAuth2GenericRESTService.authenticate((authInfo, error) => {
          if (authInfo && has(authInfo, 'access_token')) {
            const batchUpdateInfo = {
              'oauth2.access.token': authInfo.access_token,
              'vendor.auth_scheme': 'header',
              'vendor.token_type': authInfo.token_type,
              'vendor.access_token': authInfo.access_token,
              'vendor.has_token_expiration_ts': !!authInfo.expires_in,
              'vendor.token_expires_in': authInfo.expires_in,
              'vendor.refresh_token': authInfo.refresh_token,
              'vendor.last_refreshed_at': authInfo.authorizedAt,
            };
            this._handleFormFieldUpdated(
              'vendor.name',
              has(data['vendor.name']) ? data['vendor.name'] : 'oauth2_vendor_001',
              { ...data, ...batchUpdateInfo },
              batchUpdateInfo,
            );
            this._onOAuthSuccess();
          } else {
            console.log(error);
          }
        }, data);
        break;

      case OAUTH1a_REST_TEMPLATE:
        OAuth1aService.authenticate((authInfo, error) => {
          const batchUpdateInfo = {
            ...data,
            ...mapKeys(authInfo, (value, key) => `oauth1a.${key}`),
          };
          if (authInfo && has(authInfo, 'access_token')) {
            this._handleFormFieldUpdated(
              'oauth1a.access_token',
              authInfo.access_token,
              batchUpdateInfo,
            );
            this._onOAuthSuccess();
          } else {
            console.log(error);
          }
        }, data);
        break;

      case OAUTH1_GENERIC_REST_TEMPLATE:
        OAuth1GenericRESTService.authenticate((authInfo, error) => {
          if (authInfo && has(authInfo, 'access_token')) {
            const batchUpdateInfo = {
              'oauth1.access.token': authInfo.access_token,
              'oauth1.access.token.secret': authInfo.oauth_token_secret,
            };

            this._handleFormFieldUpdated('oauth1.access.token', authInfo.access_token, {
              ...data,
              ...batchUpdateInfo,
            });
            this._onOAuthSuccess();
          } else {
            console.log(error);
          }
        }, data);
        break;

      case ZENDESK_REST_TEMPLATE:
        ZendeskService.authenticate((authInfo, error) => {
          if (authInfo && has(authInfo, 'accessToken')) {
            //update zendesk token
            let access_token = authInfo.accessToken;
            this._handleFormFieldUpdated('zendesk.access_token', access_token, {
              ...data,
              'zendesk.access_token': access_token,
            });
            this._onOAuthSuccess();
            //update request headers
          } else {
            console.log(error);
          }
        }, data);
        break;

      case BOX_REST_TEMPLATE:
        BoxService.authenticate((authInfo) => {
          if (authInfo && has(authInfo, 'access_token')) {
            const batchUpdateInfo = {
              'vendor.name': 'box',
              access_token: authInfo.access_token,
              'auth.type': authInfo['auth.type'],
              'oauth2.access.token.method': authInfo['oauth2.access.token.method'],
              'oauth2.access.token.url': authInfo['oauth2.access.token.url'],
              'oauth2.client.id': authInfo['oauth2.client.id'],
              'oauth2.client.secret': authInfo['oauth2.client.secret'],
              'oauth2.token.type.override': authInfo['oauth2.token.type.override'],
              'vendor.refresh_url': authInfo['vendor.refresh_url'],
              'vendor.auth_scheme': authInfo['vendor.auth_scheme'],
              'vendor.access_token': authInfo.access_token,
              'vendor.token_type': authInfo.token_type,
              'vendor.last_refreshed_at': authInfo.authorizedAt,
              'vendor.refresh_token': authInfo.refresh_token,
              'vendor.token_expires_in': authInfo.expires_in,
              'vendor.has_token_expiration_ts': true,
              'vendor.other_info': '{}',
            };

            this._handleFormFieldUpdated(
              'vendor.name',
              'box',
              { ...data, ...batchUpdateInfo },
              batchUpdateInfo,
            );
            this._onOAuthSuccess();
          }
        }, data);
        break;

      case INTUIT_QUICKBOOKS_REST_TEMPLATE:
        IntuitService.authenticate((authInfo) => {
          if (authInfo && has(authInfo, 'access_token')) {
            let vendorOther = {
              x_refresh_token_expires_in: authInfo.x_refresh_token_expires_in,
              realmid: authInfo.realmid,
            };
            let vendorString = JSONUtils.stringifyJson(vendorOther, '');
            let batchUpdateInfo = {
              'vendor.name': INTUIT_QB_VENDOR_NAME,
              'vendor.access_token': authInfo.access_token,
              'vendor.token_type': authInfo.token_type,
              'vendor.refresh_token': authInfo.refresh_token,
              'vendor.last_refreshed_at': authInfo.authorizedAt,
              'vendor.token_expires_in': authInfo.expires_in,
              'vendor.auth_scheme': authInfo.auth_scheme,
              'vendor.scopes': authInfo.scopes,
              'vendor.has_token_expiration_ts': true,
              'vendor.other_info': `${vendorString}`,
            };

            this._handleFormFieldUpdated(
              'vendor.name',
              INTUIT_QB_VENDOR_NAME,
              { ...data, ...batchUpdateInfo },
              batchUpdateInfo,
            );
            this._onOAuthSuccess();
          }
        }, data);
        break;

      case GBQ_TEMPLATE:
        BigQueryService.authenticate((authInfo, error) => {
          if (authInfo && has(authInfo, 'access_token')) {
            const batchUpdateInfo = {
              access_token: authInfo.access_token,
              'auth.type': authInfo['auth.type'],
              'oauth2.access.token.method': authInfo['oauth2.access.token.method'],
              'oauth2.access.token.url': authInfo['oauth2.access.token.url'],
              'oauth2.client.id': authInfo['oauth2.client.id'],
              'oauth2.client.secret': authInfo['oauth2.client.secret'],
              'oauth2.token.type.override': authInfo['oauth2.token.type.override'],
              'vendor.refresh_url': authInfo['vendor.refresh_url'],
              'vendor.auth_scheme': authInfo['vendor.auth_scheme'],
              'vendor.scopes': authInfo.scope,
              'vendor.name': 'bigquery',
              'vendor.access_token': authInfo.access_token,
              'vendor.token_type': authInfo.token_type,
              'vendor.last_refreshed_at': authInfo.authorizedAt,
              'vendor.refresh_token': authInfo.refresh_token,
              'vendor.token_expires_in': authInfo.expires_in,
              'vendor.has_token_expiration_ts': true,
              'vendor.other_info': '{}',
            };

            this._handleFormFieldUpdated(
              'vendor.name',
              'bigquery',
              { ...data, ...batchUpdateInfo },
              batchUpdateInfo,
            );
            this._onOAuthSuccess();
          } else {
            console.log(error);
          }
        }, data);
        break;

      case GCS_TEMPLATE:
        GCSService.authenticate((authInfo, error) => {
          if (authInfo && has(authInfo, 'access_token')) {
            const batchUpdateInfo = {
              access_token: authInfo.access_token,
              'auth.type': authInfo['auth.type'],
              'oauth2.access.token.method': authInfo['oauth2.access.token.method'],
              'oauth2.access.token.url': authInfo['oauth2.access.token.url'],
              'oauth2.client.id': authInfo['oauth2.client.id'],
              'oauth2.client.secret': authInfo['oauth2.client.secret'],
              'oauth2.token.type.override': authInfo['oauth2.token.type.override'],
              'vendor.refresh_url': authInfo['vendor.refresh_url'],
              'vendor.auth_scheme': authInfo['vendor.auth_scheme'],
              'vendor.scopes': authInfo.scope,
              'vendor.name': 'gcs',
              'vendor.access_token': authInfo.access_token,
              'vendor.token_type': authInfo.token_type,
              'vendor.last_refreshed_at': authInfo.authorizedAt,
              'vendor.refresh_token': authInfo.refresh_token,
              'vendor.token_expires_in': authInfo.expires_in,
              'vendor.has_token_expiration_ts': true,
              'vendor.other_info': '{}',
            };

            this._handleFormFieldUpdated(
              'vendor.name',
              'gcs',
              { ...data, ...batchUpdateInfo },
              batchUpdateInfo,
            );
            this._onOAuthSuccess();
          } else {
            console.log(error);
          }
        }, data);
        break;

      case GDRIVE_TEMPLATE:
        GDriveService.authenticate((authInfo, error) => {
          if (authInfo && has(authInfo, 'access_token')) {
            const batchUpdateInfo = {
              access_token: authInfo.access_token,
              'auth.type': authInfo['auth.type'],
              'oauth2.access.token.method': authInfo['oauth2.access.token.method'],
              'oauth2.access.token.url': authInfo['oauth2.access.token.url'],
              'oauth2.client.id': authInfo['oauth2.client.id'],
              'oauth2.client.secret': authInfo['oauth2.client.secret'],
              'oauth2.token.type.override': authInfo['oauth2.token.type.override'],
              'vendor.refresh_url': authInfo['vendor.refresh_url'],
              'vendor.auth_scheme': authInfo['vendor.auth_scheme'],
              'vendor.scopes': authInfo.scope,
              'vendor.name': 'gdrive',
              'vendor.access_token': authInfo.access_token,
              'vendor.token_type': authInfo.token_type,
              'vendor.last_refreshed_at': authInfo.authorizedAt,
              'vendor.refresh_token': authInfo.refresh_token,
              'vendor.token_expires_in': authInfo.expires_in,
              'vendor.has_token_expiration_ts': true,
              'vendor.other_info': '{}',
            };

            this._handleFormFieldUpdated(
              'vendor.name',
              'gdrive',
              { ...data, ...batchUpdateInfo },
              batchUpdateInfo,
            );
            this._onOAuthSuccess();
          } else {
            console.log(error);
          }
        }, data);
        break;

      case SHAREPOINT_TEMPLATE:
        SharePointService.authenticate((authInfo, error) => {
          if (authInfo && has(authInfo, 'access_token')) {
            const batchUpdateInfo = {
              'vendor.name': 'sharepoint',
              access_token: authInfo.access_token,
              'auth.type': authInfo['auth.type'],
              'oauth2.access.token.method': authInfo['oauth2.access.token.method'],
              'oauth2.access.token.url': authInfo['oauth2.access.token.url'],
              'oauth2.client.id': authInfo['oauth2.client.id'],
              'oauth2.client.secret': authInfo['oauth2.client.secret'],
              'oauth2.token.type.override': authInfo['oauth2.token.type.override'],
              'vendor.refresh_url': authInfo['vendor.refresh_url'],
              'vendor.auth_scheme': authInfo['vendor.auth_scheme'],
              'vendor.scopes': authInfo.scope,
              'vendor.access_token': authInfo.access_token,
              'vendor.token_type': authInfo.token_type,
              'vendor.last_refreshed_at': authInfo.authorizedAt,
              'vendor.refresh_token': authInfo.refresh_token,
              'vendor.token_expires_in': authInfo.expires_in,
              'vendor.has_token_expiration_ts': true,
              'vendor.other_info': '{}',
            };
            this._handleFormFieldUpdated(
              'vendor.name',
              'sharepoint',
              { ...data, ...batchUpdateInfo },
              batchUpdateInfo,
            );
            this._onOAuthSuccess();
          } else {
            console.log(error);
          }
        }, data);
        break;

      case PUBSUB_TEMPLATE:
        PubSubService.authenticate((authInfo, error) => {
          if (authInfo && has(authInfo, 'access_token')) {
            const batchUpdateInfo = {
              access_token: authInfo.access_token,
              'auth.type': authInfo['auth.type'],
              'oauth2.access.token.method': authInfo['oauth2.access.token.method'],
              'oauth2.access.token.url': authInfo['oauth2.access.token.url'],
              'oauth2.client.id': authInfo['oauth2.client.id'],
              'oauth2.client.secret': authInfo['oauth2.client.secret'],
              'oauth2.token.type.override': authInfo['oauth2.token.type.override'],
              'vendor.refresh_url': authInfo['vendor.refresh_url'],
              'vendor.auth_scheme': authInfo['vendor.auth_scheme'],
              'vendor.scopes': authInfo.scope,
              'vendor.name': 'google_pubsub',
              'vendor.access_token': authInfo.access_token,
              'vendor.token_type': authInfo.token_type,
              'vendor.last_refreshed_at': authInfo.authorizedAt,
              'vendor.refresh_token': authInfo.refresh_token,
              'vendor.token_expires_in': authInfo.expires_in,
              'vendor.has_token_expiration_ts': true,
              'vendor.other_info': '{}',
            };

            this._handleFormFieldUpdated(
              'vendor.name',
              'google_pubsub',
              { ...data, ...batchUpdateInfo },
              batchUpdateInfo,
            );
            this._onOAuthSuccess();
          } else {
            console.log(error);
          }
        }, data);
        break;

      default:
        break;
    }
  }
}

NewCredentialsForm.defaultProp = {
  connectionType: null,
  connectionCategory: null,
};

NewCredentialsForm.propTypes = {
  selectedCredentialInfo: PropTypes.object,
  connectionAuthFields: PropTypes.array.isRequired,
  onCredInfoUpdated: PropTypes.func.isRequired,
  onCloseClicked: PropTypes.func,
  onSaveNewCredential: PropTypes.func,
};

export default withNexlaSessionToken(withSnackbar(NewCredentialsForm));
